import React, { useRef, useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { narrationVoices, GENRES, GENDERS, ACCENTS } from '../data/narrationVoices';

const { FiPlay, FiPause, FiUser, FiGlobe, FiFilter, FiCheck, FiHeadphones, FiTag } = FiIcons;

// Deterministic soft gradient per voice for the avatar tile
const AVATAR_GRADIENTS = [
  'from-primary-500 to-secondary-500',
  'from-secondary-500 to-primary-600',
  'from-primary-600 to-secondary-400',
  'from-emerald-500 to-amber-400',
  'from-teal-500 to-secondary-500',
];

const genderBadge = (g) => {
  if (g === 'Male') return 'bg-teal-50 text-teal-700 border-teal-200';
  if (g === 'Female') return 'bg-rose-50 text-rose-700 border-rose-200';
  return 'bg-gray-100 text-gray-600 border-gray-200';
};

const VoiceCard = ({ voice, index, isPlaying, isLoading, onToggle }) => {
  const grad = AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length];
  const initials = voice.name.slice(0, 2).toUpperCase();

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`flex flex-col rounded-2xl border bg-white p-5 transition-all ${
        isPlaying ? 'border-primary-400 shadow-lg ring-1 ring-primary-200' : 'border-parchment-200 shadow-sm hover:shadow-md'
      }`}
    >
      <div className="flex items-center gap-4 mb-4">
        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${grad} flex items-center justify-center text-white font-bold text-lg flex-shrink-0 shadow`}>
          {initials}
        </div>
        <div className="min-w-0">
          <h3 className="text-lg font-bold text-gray-900 leading-tight truncate">{voice.name}</h3>
          <p className="text-sm text-primary-700 font-semibold truncate">{voice.persona}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-bold border ${genderBadge(voice.gender)}`}>
          {voice.gender}
        </span>
        <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-bold border border-gray-200 bg-gray-50 text-gray-600 inline-flex items-center gap-1">
          <SafeIcon icon={FiGlobe} className="w-2.5 h-2.5" /> {voice.accent}
        </span>
        <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-bold border border-secondary-200 bg-secondary-50 text-secondary-700 inline-flex items-center gap-1">
          <SafeIcon icon={FiTag} className="w-2.5 h-2.5" /> {voice.genre}
        </span>
      </div>

      <p className="text-sm text-gray-600 leading-relaxed mb-5 flex-grow">{voice.desc}</p>

      <button
        type="button"
        onClick={() => onToggle(voice)}
        aria-label={isPlaying ? `Pause ${voice.name} sample` : `Play ${voice.name} sample`}
        className={`mt-auto inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg font-bold text-sm transition-all shadow-sm ${
          isPlaying
            ? 'bg-secondary-500 text-white hover:bg-secondary-600'
            : 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white hover:opacity-90'
        }`}
      >
        {isLoading ? (
          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : isPlaying ? (
          <>
            <SafeIcon icon={FiPause} className="w-4 h-4" /> Pause sample
            <span className="flex items-end gap-0.5 h-3 ml-1">
              {[...Array(4)].map((_, i) => (
                <motion.span key={i} className="w-0.5 bg-white rounded-full"
                  animate={{ height: [4, 12, 6, 10, 4] }}
                  transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.1 }} />
              ))}
            </span>
          </>
        ) : (
          <><SafeIcon icon={FiPlay} className="w-4 h-4" /> Play sample</>
        )}
      </button>
    </motion.div>
  );
};

const Chip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all ${
      active
        ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white border-transparent shadow-sm'
        : 'bg-white text-gray-600 border-parchment-200 hover:border-primary-300 hover:text-primary-700'
    }`}
  >
    {children}
  </button>
);

const NarrationVoiceBrowser = ({ voices = narrationVoices, genres = GENRES, genders = GENDERS, accents = ACCENTS } = {}) => {
  const [genre, setGenre] = useState('all');
  const [gender, setGender] = useState('all');
  const [accent, setAccent] = useState('all');
  const [playingId, setPlayingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const filtered = useMemo(() => voices.filter((v) =>
    (genre === 'all' || v.genre === genre) &&
    (gender === 'all' || v.gender === gender) &&
    (accent === 'all' || v.accent === accent)
  ), [voices, genre, gender, accent]);

  const toggle = (voice) => {
    const a = audioRef.current;
    if (!a) return;
    if (playingId === voice.id) {
      a.pause();
      setPlayingId(null);
      return;
    }
    a.pause();
    a.src = voice.preview;
    a.currentTime = 0;
    setLoadingId(voice.id);
    a.play()
      .then(() => { setPlayingId(voice.id); setLoadingId(null); })
      .catch(() => { setPlayingId(null); setLoadingId(null); });
  };

  return (
    <div>
      {/* Filter bar */}
      <div className="rounded-2xl border border-parchment-200 bg-white/80 backdrop-blur p-5 mb-8 shadow-sm">
        <div className="flex items-center gap-2 text-gray-700 font-semibold mb-4">
          <SafeIcon icon={FiFilter} className="w-5 h-5 text-primary-600" />
          <span>Filter narrators</span>
          <span className="ml-auto inline-flex items-center text-sm text-gray-500 bg-parchment-50 border border-parchment-200 px-3 py-1 rounded-full">
            <SafeIcon icon={FiCheck} className="w-4 h-4 mr-1.5 text-primary-500" />
            <span className="font-bold text-gray-900 mr-1">{filtered.length}</span> voices
          </span>
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wide text-gray-400 w-16">Genre</span>
            <Chip active={genre === 'all'} onClick={() => setGenre('all')}>All</Chip>
            {genres.filter((g) => g !== 'All').map((g) => <Chip key={g} active={genre === g} onClick={() => setGenre(g)}>{g}</Chip>)}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wide text-gray-400 w-16">Voice</span>
            <Chip active={gender === 'all'} onClick={() => setGender('all')}>All</Chip>
            {genders.filter((g) => g !== 'All').map((g) => <Chip key={g} active={gender === g} onClick={() => setGender(g)}>{g}</Chip>)}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wide text-gray-400 w-16">Accent</span>
            <Chip active={accent === 'all'} onClick={() => setAccent('all')}>All</Chip>
            {accents.filter((a) => a !== 'All').map((a) => <Chip key={a} active={accent === a} onClick={() => setAccent(a)}>{a}</Chip>)}
          </div>
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-parchment-200">
          <SafeIcon icon={FiHeadphones} className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 mb-3">No narrators match those filters.</p>
          <button
            onClick={() => { setGenre('all'); setGender('all'); setAccent('all'); }}
            className="text-primary-600 font-semibold underline hover:text-primary-700"
          >Clear filters</button>
        </div>
      ) : (
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((voice, i) => (
            <VoiceCard
              key={voice.id}
              voice={voice}
              index={i}
              isPlaying={playingId === voice.id}
              isLoading={loadingId === voice.id}
              onToggle={toggle}
            />
          ))}
        </motion.div>
      )}

      <p className="text-xs text-gray-400 mt-6 flex items-center gap-1.5">
        <SafeIcon icon={FiUser} className="w-3.5 h-3.5" />
        Sample previews are representative AI narration voices. Your finished audiobook is rendered from the voice you pick.
      </p>

      <audio ref={audioRef} onEnded={() => setPlayingId(null)} preload="none" crossOrigin="anonymous" />
    </div>
  );
};

export default NarrationVoiceBrowser;
